import { MessagePacking, type ActionFailurePm } from '@core/feedback/feedback.packing'
import type { GatewayFailure, GatewayResponse } from './api-response.types'

/**
 * Retryability of a mechanism-level failure (async-operation-system-
 * specification.md §6.2): true for a network transport failure, or any
 * HTTP 500-599 response — enveloped or not. A 503 from the backend's own
 * error-handler (a down DB/dependency, see backend/src/middleware/error-
 * handler.ts) is a real `{success:false}` envelope, not a raw transport
 * failure, but it's exactly as transient as an unenveloped 5xx — the
 * envelope is a wire-format detail, not a retryability signal. Everything
 * else (4xx of any kind, an invalid/unparseable response, a cancelled
 * transport) is non-retryable — retrying it would either repeat a request
 * the server already understood and rejected, or repeat work that was
 * deliberately stopped.
 */
export function isRetryableGatewayFailure(failure: GatewayFailure): boolean {
  if (failure.kind === 'rejected') return failure.status >= 500 && failure.status <= 599
  if (failure.kind !== 'transport') return false
  if (failure.reason === 'network') return true
  if (failure.reason === 'http') return failure.status !== undefined && failure.status >= 500 && failure.status <= 599
  return false
}

/**
 * Maps a failed GatewayResponse to the Failure shape a retry-bearing read
 * operation's `execute()` returns (spec §8.1's `books.load`/`assets.load`).
 * One shared implementation instead of every read operation reconstructing
 * "unpack the mechanism failure, then bolt on `retryable`" by hand — the two
 * existing copies of this were identical, and a third divergent copy
 * wouldn't fail any test, it would just silently retry (or not) inconsistently.
 */
export function mapReadFailure(
  response: Extract<GatewayResponse, { success: false }>,
): ActionFailurePm & Readonly<{ retryable: boolean }> {
  const pm = MessagePacking.unpackGatewayResponseToPm(response)
  return {
    ...(pm.success ? { code: 'temporarily-unavailable' as const } : pm.failure),
    retryable: isRetryableGatewayFailure(response.failure),
  }
}
